/**
 * Bill document hero - `prototype/purchases/bill-detail.html` document-first layout.
 *
 * Vendor letterhead is shown at the top, "Bill to" is the tenant company, and
 * line items render from the persisted purchase-side `bill_lines` snapshots.
 */
import { initials } from "../../../lib/initials.ts";
import { formatDate, paiseToInrExact } from "../../../lib/format.ts";

/** Data needed to render the printable bill paper. */
export interface BillDocProps {
  bill: {
    id: string;
    vendorName: string;
    vendorGstin: string | null;
    vendorCity: string | null;
    vendorState: string | null;
    vendorDocNumber: string | null;
    receivedOn: string;
    dueOn: string | null;
    subtotalPaise: string;
    taxPaise: string;
    totalPaise: string;
    vendorNote: string | null;
    threeWayMatch: "matched" | "mismatch" | "na";
    source: "photo" | "email_pdf" | "manual";
    overallConfidence: string | null;
    lineItems: Array<{
      id: string;
      position: number;
      description: string;
      hsn: string;
      qtyMilli: number;
      unit: string;
      ratePaise: string;
      taxRateBps: number;
      taxPaise: string;
      lineTotalPaise: string;
    }>;
  };
  company: {
    legalName: string;
    address: string | null;
    city: string | null;
    state: string | null;
    pincode: string | null;
    gstin: string | null;
    bankName: string | null;
    bankAccountNumber: string | null;
    bankIfsc: string | null;
    bankUpiId: string | null;
  };
}

/** Render the bill document hero. */
export function BillDoc({ bill, company }: BillDocProps) {
  const docNumber = bill.vendorDocNumber ?? "Vendor bill";
  const vendorAddress = [bill.vendorCity, bill.vendorState].filter(Boolean).join(", ");
  const buyerAddress = [company.address, company.city, company.state, company.pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <article className="doc" aria-label="Printable bill document">
      <div className="doc-bar">
        <span className="filename">
          <strong>{docNumber}.pdf</strong> - vendor original
        </span>
      </div>
      <div className="doc-paper">
        <header className="doc-letterhead">
          <div className="seller">
            <div className="logo" aria-hidden>
              {initials(bill.vendorName)}
            </div>
            <h2>{bill.vendorName}</h2>
            <div className="addr">
              {vendorAddress ? <strong>{vendorAddress}</strong> : null}
              {bill.vendorGstin ? (
                <>
                  <br />
                  GSTIN&nbsp;<span className="num">{bill.vendorGstin}</span>
                </>
              ) : (
                <>
                  <br />
                  GSTIN not on file
                </>
              )}
            </div>
          </div>
          <div className="marker">
            <div className="label">Tax Invoice (vendor)</div>
            <h1>{docNumber}</h1>
            <div className="num-line">
              <span className="k">Received</span>
              {formatDate(bill.receivedOn)}
            </div>
            {bill.dueOn ? (
              <div className="num-line">
                <span className="k">Due</span>
                {formatDate(bill.dueOn)}
              </div>
            ) : null}
            <div className="internal">
              Our ref - <strong>{bill.id.slice(0, 8).toUpperCase()}</strong>
            </div>
          </div>
        </header>

        <div className="doc-meta-grid">
          <div className="to">
            <div className="lbl">Bill to</div>
            <div className="name">{company.legalName}</div>
            <div className="addr">
              {buyerAddress ? <strong>{buyerAddress}</strong> : "Address not configured"}
              {company.gstin ? (
                <span className="num">GSTIN {company.gstin}</span>
              ) : (
                <span className="num">GSTIN not configured</span>
              )}
            </div>
          </div>
          <div className="dates">
            <div className="cell">
              <div className="k">Received</div>
              <div className="v">{formatDate(bill.receivedOn)}</div>
              <div className="sub">{sourceLabel(bill.source)}</div>
            </div>
            <div className="cell">
              <div className="k">Due</div>
              <div className="v">{bill.dueOn ? formatDate(bill.dueOn) : "-"}</div>
            </div>
            <div className="cell">
              <div className="k">3-way match</div>
              <div className="v">{matchLabel(bill.threeWayMatch)}</div>
            </div>
            <div className="cell">
              <div className="k">Vendor note</div>
              <div className="v">{bill.vendorNote ?? "-"}</div>
            </div>
          </div>
        </div>

        <table className="doc-items" aria-label="Bill line items">
          <thead>
            <tr>
              <th>Description</th>
              <th>HSN</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Tax</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lineItems.length > 0 ? (
              bill.lineItems.map((line) => (
                <tr key={line.id}>
                  <td>{line.description}</td>
                  <td className="num">{line.hsn || "-"}</td>
                  <td className="num">
                    {formatQty(line.qtyMilli)} {line.unit}
                  </td>
                  <td className="num">{withoutRupee(paiseToInrExact(line.ratePaise))}</td>
                  <td className="num">
                    {formatBps(line.taxRateBps)}% GST
                    <br />
                    <span>{withoutRupee(paiseToInrExact(line.taxPaise))}</span>
                  </td>
                  <td className="num">{withoutRupee(paiseToInrExact(line.lineTotalPaise))}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  Line items are not available for this older bill. Edit and save the bill to
                  persist item rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="doc-totals">
          <div className="note">
            <strong>Tax mode - IGST / CGST+SGST</strong> - derived from vendor billing branch. Full
            GST is claimable as <strong>input tax credit</strong> in GSTR-3B once approved.
          </div>
          <div className="stack">
            <div className="row">
              <span>Subtotal - taxable value</span>
              <span className="v">{withoutRupee(paiseToInrExact(bill.subtotalPaise))}</span>
            </div>
            <div className="row">
              <span>GST</span>
              <span className="v">{withoutRupee(paiseToInrExact(bill.taxPaise))}</span>
            </div>
            <div className="row grand">
              <span>Bill total</span>
              <span className="v">{paiseToInrExact(bill.totalPaise)}</span>
            </div>
          </div>
        </div>

        <div className="doc-foot">
          <div>
            <strong style={{ color: "var(--ink-2)" }}>Vendor's terms.</strong> Payable by NEFT /
            RTGS to the account on the right. Quote vendor invoice number in the bank reference.
          </div>
          <div className="pay">
            <span className="k">Beneficiary</span> <strong>{bill.vendorName}</strong>
            <br />
            <span className="k">A/c</span> <span>per vendor's invoice</span>
            <br />
            <span className="k">Bill to</span> <strong>{company.legalName}</strong>
            <br />
            <span className="k">Bank</span> {company.bankName ?? "-"}
          </div>
        </div>
      </div>
    </article>
  );
}

function withoutRupee(value: string): string {
  return value.replace("₹", "").trim();
}

function formatQty(qtyMilli: number): string {
  const whole = Math.trunc(qtyMilli / 1000);
  const remainder = qtyMilli % 1000;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(3, "0").replace(/0+$/, "")}`;
}

function formatBps(bps: number): string {
  const whole = Math.trunc(bps / 100);
  const remainder = bps % 100;
  if (remainder === 0) return String(whole);
  return `${whole}.${String(remainder).padStart(2, "0").replace(/0+$/, "")}`;
}

function sourceLabel(source: "photo" | "email_pdf" | "manual"): string {
  if (source === "photo") return "Photo upload";
  if (source === "email_pdf") return "Email PDF";
  return "Manual entry";
}

function matchLabel(match: "matched" | "mismatch" | "na"): string {
  if (match === "matched") return "Matched";
  if (match === "mismatch") return "Mismatch";
  return "Not applicable";
}
